import { useState } from 'react'
import { isEmailNotConfirmedError } from '@prosperia/shared'
import { useSupabaseAuth } from '../lib/useSupabaseAuth'
import { supabase } from '../lib/supabase'
import { Button } from './ui/button'

const SupabaseLogin = ({ onSuccess, redirectTo = '/dashboard', initialEmail = '', notice = '' }) => {
  const { signIn, resetPassword, loading } = useSupabaseAuth()
  const [email, setEmail] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [appleLoading, setAppleLoading] = useState(false)
  const [needsConfirmation, setNeedsConfirmation] = useState(false)
  const [resendState, setResendState] = useState('idle') // idle | sending | sent
  const [resetState, setResetState] = useState('idle') // idle | sending | sent

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setNeedsConfirmation(false)

    try {
      const result = await signIn(email, password)
      
      if (result.success) {
        if (onSuccess) {
          onSuccess(result.data)
        } else {
          window.location.href = redirectTo
        }
      } else if (isEmailNotConfirmedError({ code: result.code, message: result.error })) {
        // Email signups stay unconfirmed until the verification link is
        // clicked (#428).
        setNeedsConfirmation(true)
        setResendState('idle')
      } else {
        setError(result.error || 'Sign in failed')
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred')
    }
  }

  const handleResendVerification = async () => {
    setResendState('sending')
    setError('')
    const { error: resendError } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim(),
      options: { emailRedirectTo: `${window.location.origin}/dashboard` },
    })
    if (resendError) {
      console.error('[SupabaseLogin] Resend verification failed:', resendError)
      setError(resendError.message || 'Could not resend the verification email')
      setResendState('idle')
      return
    }
    setResendState('sent')
  }

  const handleForgotPassword = async () => {
    setError('')
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setError('Enter your email address above, then choose "Forgot password?"')
      return
    }
    setResetState('sending')
    const result = await resetPassword(trimmedEmail)
    if (!result.success) {
      console.error('[SupabaseLogin] Password reset failed:', result.error)
      setError(result.error || 'Could not send the reset email')
      setResetState('idle')
      return
    }
    setResetState('sent')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) {
      const form = e.target.closest('form')
      if (form) {
        form.requestSubmit()
      }
    }
  }

  const handleAppleSignIn = async () => {
    try {
      setAppleLoading(true)
      setError('')

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      })

      if (error) throw error
      // Supabase redirects to Apple's auth page automatically
    } catch (err) {
      setError(err.message || 'Apple sign in failed')
      setAppleLoading(false)
    }
  }

  return (
    <div className="w-full space-y-6">
      <div className="text-center">
        <p className="text-sm text-muted">
          Enter your credentials to access your dashboard
        </p>
      </div>

      {notice && (
        <div role="status" className="rounded-lg bg-teal-900/20 border border-teal-500/30 p-4">
          <p className="text-sm text-teal-200 font-medium">{notice}</p>
        </div>
      )}

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-secondary mb-2">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="w-full px-4 py-3 bg-elevated/50 border border-mute rounded-lg text-primary placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              aria-label="Email address"
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-secondary mb-2">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="w-full px-4 py-3 bg-elevated/50 border border-mute rounded-lg text-primary placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              aria-label="Password"
            />
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={handleForgotPassword}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleForgotPassword() } }}
                disabled={resetState === 'sending'}
                tabIndex={0}
                aria-label="Forgot password? Email me a reset link"
                className="min-h-[44px] px-1 text-sm font-medium text-teal-300 hover:text-teal-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetState === 'sending' ? 'Sending reset link...' : 'Forgot password?'}
              </button>
            </div>
          </div>
        </div>

        {resetState === 'sent' && (
          <div role="status" className="rounded-lg bg-teal-900/20 border border-teal-500/30 p-4">
            <p className="text-sm text-teal-200 font-medium">
              If an account exists for {email.trim()}, a password reset link is on its way. Check your inbox and spam folder.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-900/20 border border-red-500/30 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <i className="fas fa-exclamation-circle text-red-400 text-lg"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-300 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {needsConfirmation && (
          <div role="alert" className="rounded-lg bg-amber-900/20 border border-amber-500/30 p-4 space-y-3">
            <p className="text-sm text-amber-200 font-medium">
              Please verify your email before signing in. Click the link we sent to {email.trim()}.
            </p>
            {resendState === 'sent' ? (
              <p className="text-sm text-amber-100">A new verification link is on its way. Check your inbox and spam folder.</p>
            ) : (
              <button
                type="button"
                onClick={handleResendVerification}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResendVerification() }}
                disabled={resendState === 'sending'}
                tabIndex={0}
                aria-label="Resend verification email"
                className="min-h-[44px] w-full rounded-lg border border-amber-500/40 px-4 py-2 text-sm font-semibold text-amber-100 hover:bg-amber-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendState === 'sending' ? 'Sending...' : 'Resend verification email'}
              </button>
            )}
          </div>
        )}

        <div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-semibold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            tabIndex={0}
            aria-label="Sign in"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Signing in...
              </div>
            ) : (
              <span className="flex items-center justify-center">
                <i className="fas fa-sign-in-alt mr-2"></i>
                Sign In
              </span>
            )}
          </Button>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-mute"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-elevated text-muted">or</span>
          </div>
        </div>

        {/* Apple Sign In */}
        <div>
          <button
            type="button"
            onClick={handleAppleSignIn}
            disabled={loading || appleLoading}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-hover text-black font-semibold py-3 px-4 rounded-lg shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            tabIndex={0}
            aria-label="Sign in with Apple"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAppleSignIn() }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            {appleLoading ? 'Connecting...' : 'Sign in with Apple'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default SupabaseLogin 